import { Ticket } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import type { BusinessDiscounts } from '../../types/businessMetrics';
import {
  EMPTY_VALUE_LABEL,
  formatBusinessCount,
  formatBusinessDateTime,
  formatBusinessMoney,
} from '../../types/businessMetricsView';
import { BusinessPanel, NoDataNote, NotApplicableNote, StatGrid, StatTile } from './businessPanelParts';

function FounderSeats({ founder }: { founder: BusinessDiscounts['founder'] }) {
  const codes = founder.codes.length > 0 ? founder.codes.join(', ') : EMPTY_VALUE_LABEL;
  return (
    <StatGrid>
      <StatTile
        label={`창립 멤버 ${formatBusinessCount(founder.limit)}명 중 남은 자리`}
        value={formatBusinessCount(founder.remaining)}
        hint={`코드 ${codes}`}
        tone={founder.remaining !== null && founder.remaining <= 10 ? 'warning' : 'default'}
      />
      <StatTile label="창립 멤버 사용" value={formatBusinessCount(founder.used)} />
    </StatGrid>
  );
}

/** checkout_code_applications.source 표시 이름 */
const CODE_SOURCE_LABELS: Record<string, string> = { PROMO: '캠페인', REFERRAL: '추천' };

export function DiscountPanel({ discounts }: { discounts: BusinessDiscounts | null }) {
  if (!discounts) {
    return (
      <BusinessPanel title="할인 코드" testId="business-discount-panel" icon={Ticket}>
        <NotApplicableNote reason="이 앱에는 할인 코드 데이터 경로가 없습니다." />
      </BusinessPanel>
    );
  }
  return (
    <BusinessPanel
      title="할인 코드"
      testId="business-discount-panel"
      icon={Ticket}
      footer="사용·남은 수는 Paddle 카탈로그(없으면 설정+원장), 결제창 적용은 코드가 붙은 결제창 수, 원장 사용·할인액은 결제 완료 기준입니다."
    >
      <div className="space-y-3">
        {discounts.catalogStatus === 'UNAVAILABLE' ? <NoDataNote message={discounts.catalogMessage} /> : null}
        <FounderSeats founder={discounts.founder} />
        {discounts.codes.length === 0 ? (
          <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">할인 코드가 없습니다.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table aria-label="할인 코드별 사용 현황" className="min-w-[700px]">
              <TableHeader>
                <TableRow>
                  <TableHead>코드</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead className="whitespace-nowrap text-right">사용 / 한도</TableHead>
                  <TableHead className="whitespace-nowrap text-right">남은 수</TableHead>
                  <TableHead className="whitespace-nowrap text-right">결제창 적용</TableHead>
                  <TableHead className="whitespace-nowrap text-right">원장 사용</TableHead>
                  <TableHead className="whitespace-nowrap text-right">할인액</TableHead>
                  <TableHead>만료</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {discounts.codes.map((code) => (
                  <TableRow key={code.discountId}>
                    <TableCell>
                      <span className="font-medium">{code.code ?? EMPTY_VALUE_LABEL}</span>
                      <p className="text-xs text-muted-foreground">{code.discountId}</p>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {code.status ?? EMPTY_VALUE_LABEL}
                      {code.source ? <p className="text-xs text-muted-foreground">{CODE_SOURCE_LABELS[code.source] ?? code.source}</p> : null}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums">
                      {formatBusinessCount(code.timesUsed)} / {code.usageLimit === null ? EMPTY_VALUE_LABEL : formatBusinessCount(code.usageLimit)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums">{formatBusinessCount(code.remaining)}</TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums">{formatBusinessCount(code.checkoutApplications)}</TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums">{formatBusinessCount(code.ledgerUses)}</TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums">
                      {code.currency === null ? EMPTY_VALUE_LABEL : formatBusinessMoney(code.ledgerDiscountTotal, code.currency)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs">{formatBusinessDateTime(code.expiresAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </BusinessPanel>
  );
}
